import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import {
  ConfigPackLoader,
  type CanonicalEdge,
  type CanonicalNode,
  type CanonicalWorkflowDocument,
  type FormDocument,
  type FormFieldDef,
  type FormSectionDef,
} from '@los-workspace/workflow-runtime';
import { firstValueFrom } from 'rxjs';
import type { TableColumn } from '@shared/table/table';
import type { TabItem } from '@shared/tabs/tabs.model';
import type { DropdownOption } from '@shared/dropdown/dropdown.model';
import type { PackDataSourceVars } from './pack-data-source.service';
import { resolvePackEndpoint } from './pack-data-source.service';
import {
  buildSubmitPayload,
  parseSubmitPayloadMapping,
  type SubmitFieldMapping,
} from '../components/workflow-pack-form/pack-workflow-submit.mapper';
import {
  buildFieldCatalogMap,
  enrichFieldFromCatalog,
} from '../components/workflow-pack-form/pack-field-catalog';
import {
  type OpportunityDetailAction,
  type OpportunityView,
  type OpportunityViewParams,
} from './opportunity-navigation.state';
import { coerceText } from '../utils/coerce-text';
import { uiApplicantType } from '../utils/opportunity-applicant-type.util';
import { presentOpportunityListColumn } from '../utils/opportunity-list-table.columns';
import { normalizePageFormDocument } from '../../loan-application/utils/page-form-document.util';
import {
  discoverOpportunityWorkflowRoles,
  discoverSubmitNodeKey,
  isCreateExistingPartyFormNode,
  isOmittedOpportunityListRowAction,
  normalizeStatusIntent,
  normalizeTypeIntent,
  opportunityActionIntent,
  resolveListActionHandle,
  type OpportunityWorkflowRoles,
} from '../utils/opportunity-workflow-nodes';
import {
  buildDetailResponseFieldMapping,
  buildDetailHeaderSummaryFromViewForm,
  buildDetailTabsFromFormView,
  finalizeDetailTabConfigs,
  buildDetailTabsFromRecordDetail,
  type DetailHeaderSummaryItem,
  type DetailTabConfig,
} from '../utils/opportunity-detail-display.util';

export type DatasetColumnConfig = {
  columnKey: string;
  label: string;
  sourcePath: string;
  dataType?: string;
  sortable?: boolean;
  visible?: boolean;
  filter?: boolean;
  filterOptions?: Array<{ label: string; value: string }>;
  search?: boolean;
  width?: number | string;
  align?: string;
};

export type DatasetFilterConfig = {
  key: string;
  label: string;
  control: string;
  paramName?: string;
  defaultValue?: unknown;
  optionsConfig?: {
    options?: Array<{ label: string; value: string }>;
    source?: string;
  };
};

export type DatasetActionConfig = {
  key: string;
  label: string;
  scope?: string;
  type?: string;
  confirm?: boolean;
  config?: Record<string, unknown>;
};

export type DatasetUiConfig = {
  general: {
    title?: string;
    description?: string;
    defaultPageSize?: number;
    searchEnabled?: boolean;
    rowIdColumn?: string;
    toolbar?: { createButtonLabel?: string; createButtonEnabled?: boolean };
  };
  columns: DatasetColumnConfig[];
  filters: DatasetFilterConfig[];
  actions: DatasetActionConfig[];
};

export type ChoiceUiConfig = {
  prompt?: string;
  fieldCode: string;
  options: string[];
  optionDescriptions?: Record<string, string>;
  continueLabel?: string;
  cancelLabel?: string;
};

export type RecordDetailUiConfig = {
  general?: { title?: string; description?: string; backLabel?: string };
  tabs: Array<{
    key: string;
    label: string;
    sections?: Array<{
      key: string;
      label: string;
      fields?: Array<{
        key: string;
        label: string;
        sourcePath: string;
        dataType?: string;
        fullWidth?: boolean;
      }>;
    }>;
  }>;
  actions: Array<{ key: string; label: string; scope?: string; icon?: string; confirm?: boolean }>;
  header?: {
    titlePath?: string;
    subtitlePath?: string;
    referencePath?: string;
    avatarPath?: string;
    summary?: Array<{ key: string; label: string; sourcePath: string; dataType?: string }>;
    badges?: Array<{ key: string; label: string; sourcePath: string; badgeVariant?: string }>;
  };
  milestones?: { title?: string; collectionPath?: string };
};

export type SearchFieldUi = {
  fieldCode: string;
  label: string;
  placeholder?: string;
  control: 'text' | 'date' | 'phone';
};

export type CustomerSearchUiConfig = {
  title: string;
  description: string;
  createActionLabel: string;
  emptyStateText: string;
};

export type FieldLabelUi = {
  label: string;
  placeholder?: string;
  required?: boolean;
};

/**
 * Loads OPPORTUNITY workflow-config and exposes UI bindings for existing pages.
 * This is the layer that makes list/modal/create/details read from the pack,
 * not hardcoded Angular constants.
 */
@Injectable({ providedIn: 'root' })
export class OpportunityWorkflowConfigService {
  private readonly pack = inject(ConfigPackLoader);
  private readonly http = inject(HttpClient);

  private workflow: CanonicalWorkflowDocument | null = null;
  private resolvedWorkflowKey: string | null = null;
  private ready = false;
  private roles: OpportunityWorkflowRoles = discoverOpportunityWorkflowRoles(null);
  private readonly nodesByKey = new Map<string, CanonicalNode>();
  private readonly outgoingByKey = new Map<string, CanonicalEdge[]>();

  readonly dataset = signal<DatasetUiConfig | null>(null);
  readonly choice = signal<ChoiceUiConfig | null>(null);
  readonly statusIndividual = signal<ChoiceUiConfig | null>(null);
  readonly statusCorporate = signal<ChoiceUiConfig | null>(null);
  readonly recordDetail = signal<RecordDetailUiConfig | null>(null);

  async ensureLoaded(): Promise<void> {
    if (this.ready) return;
    await this.pack.load();
    this.resolvedWorkflowKey = await this.pack.resolveWorkflowKey('OPPORTUNITY');
    this.workflow = await this.pack.getWorkflow(this.resolvedWorkflowKey);
    this.rebuildWorkflowIndexes();
    this.roles = discoverOpportunityWorkflowRoles(this.workflow);
    this.dataset.set(this.readDataset(this.roles.listNodeKey));
    this.choice.set(this.readChoice(this.roles.applicantTypeNodeKey, 'applicantTypes'));
    this.statusIndividual.set(
      this.readChoice(this.roles.statusNodeKeys.individual, 'statusOptions'),
    );
    this.statusCorporate.set(
      this.readChoice(this.roles.statusNodeKeys.corporate, 'statusOptions'),
    );
    this.recordDetail.set(
      this.readRecordDetail(
        this.roles.viewNodeKeys[0] ?? this.getNode('detail_opportunity')?.nodeKey,
      ),
    );
    this.ready = true;
  }

  workflowRoles(): OpportunityWorkflowRoles {
    return this.roles;
  }

  listNodeKey(): string | null {
    return this.roles.listNodeKey;
  }

  isListNode(nodeKey: string | null | undefined): boolean {
    return !!nodeKey && nodeKey === this.roles.listNodeKey;
  }

  isApplicantTypeNode(nodeKey: string | null | undefined): boolean {
    return !!nodeKey && nodeKey === this.roles.applicantTypeNodeKey;
  }

  isStatusNode(nodeKey: string | null | undefined): 'individual' | 'corporate' | null {
    if (!nodeKey) return null;
    if (nodeKey === this.roles.statusNodeKeys.corporate) return 'corporate';
    if (nodeKey === this.roles.statusNodeKeys.individual) return 'individual';
    return null;
  }

  isSearchNode(nodeKey: string | null | undefined): boolean {
    return !!nodeKey && this.roles.searchNodeKeys.includes(nodeKey);
  }

  isCreateNode(nodeKey: string | null | undefined): boolean {
    if (!nodeKey) return false;
    return this.roles.createNodeKeys.includes(nodeKey) || this.isCreateExistingFormNode(nodeKey);
  }

  isViewNode(nodeKey: string | null | undefined): boolean {
    return !!nodeKey && (this.roles.viewNodeKeys.includes(nodeKey) || nodeKey === 'detail_opportunity');
  }

  isEditNode(nodeKey: string | null | undefined): boolean {
    return !!nodeKey && this.roles.editNodeKeys.includes(nodeKey);
  }

  /** True when the form loads an opportunity record for update (`/opportunities/{{opportunityId}}`). */
  isOpportunityRecordEditFormNode(nodeKey: string | null | undefined): boolean {
    if (!nodeKey) {
      return false;
    }
    return /\/opportunities\/\{\{opportunityId\}\}/i.test(this.readNodeDataSourceEndpoint(nodeKey));
  }

  /**
   * True when paused on an opportunity edit form — excludes create-new and
   * create-existing party forms even when designer `mode` is `"edit"`.
   */
  isEditFormPauseNode(nodeKey: string | null | undefined): boolean {
    if (!nodeKey || this.isCreateExistingFormNode(nodeKey)) {
      return false;
    }
    if (this.isEditNode(nodeKey)) {
      return true;
    }
    return (
      this.formNodeMode(nodeKey) === 'edit' && this.isOpportunityRecordEditFormNode(nodeKey)
    );
  }

  /** True when paused on a create-new or create-existing party form. */
  isCreateFormPauseNode(nodeKey: string | null | undefined): boolean {
    if (!nodeKey) {
      return false;
    }
    return (
      this.isCreateExistingFormNode(nodeKey) ||
      this.isCreateNode(nodeKey) ||
      this.formNodeMode(nodeKey) === 'create'
    );
  }

  /** Brand-new party create form only (excludes existing-customer load/prefill nodes). */
  isNewCustomerCreateFormNode(nodeKey: string | null | undefined): boolean {
    if (!nodeKey || this.isCreateExistingFormNode(nodeKey)) {
      return false;
    }
    return (
      this.roles.createNodeKeys.includes(nodeKey) &&
      !nodeKey.includes('existing')
    );
  }

  /** Canonical new/existing intent from workflow context (handles JSON option labels). */
  readApplicantStatusFromContext(ctx: Record<string, unknown>): 'new' | 'existing' {
    return normalizeStatusIntent(
      coerceText(ctx['applicantStatus'] ?? ctx['statusOptions'] ?? ctx['customerStatus'] ?? ''),
    );
  }

  /** Read opportunity id from workflow context (handles JSON fieldMapping aliases). */
  readOpportunityIdFromContext(ctx: Record<string, unknown>): string {
    const explicit = coerceText(ctx['opportunityId'] ?? ctx['opportunityID'] ?? '');
    if (explicit) {
      return explicit;
    }

    const row = ctx['selectedRow'];
    if (row && typeof row === 'object') {
      const record = row as Record<string, unknown>;
      const rowOpportunityId = coerceText(record['opportunityId'] ?? record['opportunityID'] ?? '');
      if (rowOpportunityId) {
        return rowOpportunityId;
      }
      const rowId = coerceText(record['id'] ?? '');
      if (rowId && this.looksLikeOpportunityListRow(record)) {
        return rowId;
      }
    }

    if (this.isPartyScopedCreateContext(ctx)) {
      return '';
    }

    return coerceText(ctx['id'] ?? '');
  }

  private looksLikeOpportunityListRow(row: Record<string, unknown>): boolean {
    return !!(
      row['opportunityNumber'] ??
      row['opportunityId'] ??
      row['opportunityID'] ??
      row['status'] ??
      row['product'] ??
      row['productLabel'] ??
      row['priorityCode']
    );
  }

  /** Create-existing flows store party UUID in `id` — not an opportunity id. */
  private isPartyScopedCreateContext(ctx: Record<string, unknown>): boolean {
    const actionKey = coerceText(ctx['actionKey']).toLowerCase();
    if (
      actionKey === 'view_details' ||
      actionKey === 'edit' ||
      actionKey === 'action2' ||
      opportunityActionIntent(actionKey, this.listRowActions()) === 'view' ||
      opportunityActionIntent(actionKey, this.listRowActions()) === 'edit'
    ) {
      return false;
    }
    if (actionKey === 'create_opportunity' || actionKey === 'action1') {
      return true;
    }
    if (this.readApplicantStatusFromContext(ctx) === 'existing' && ctx['formSubmitted'] !== true) {
      return true;
    }
    return false;
  }

  formNodeMode(nodeKey: string): string | null {
    const mode = this.getNode(nodeKey)?.configuration?.['mode'];
    return typeof mode === 'string' ? mode.toLowerCase() : null;
  }

  /** Map a paused workflow form node to a detail-page action when label/key indicates it. */
  detailActionForFormNode(nodeKey: string): OpportunityDetailAction | null {
    const node = this.getNode(nodeKey);
    if (!node) return null;
    const blob = `${node.nodeKey} ${node.label ?? ''}`.toLowerCase();
    if (blob.includes('assign')) return 'assign_to_rm';
    if (blob.includes('follow')) return 'follow_up';
    if (blob.includes('reject')) return 'reject_opportunity';
    return null;
  }

  listActionKeyForIntent(
    intent: ReturnType<typeof opportunityActionIntent>,
  ): string | undefined {
    const actions = this.listRowActions();
    return actions.find((action) => opportunityActionIntent(action.key, actions) === intent)
      ?.key;
  }

  /**
   * Resolve the Opportunities shell major view from a paused workflow node
   * using node type/configuration metadata — not hardcoded designer node ids.
   */
  shellViewForPausedNode(
    nodeKey: string,
    ctx: Record<string, unknown>,
    extra: Partial<OpportunityViewParams> = {},
  ): { view: OpportunityView; params: Partial<OpportunityViewParams> } | null {
    if (this.isListNode(nodeKey)) {
      return { view: 'list', params: extra };
    }
    if (this.isCreateModalNode(nodeKey)) {
      return { view: 'list', params: { wfStep: nodeKey, ...extra } };
    }
    if (this.isCustomerDatasetNode(nodeKey)) {
      return {
        view: 'customer-search',
        params: {
          applicantType: normalizeTypeIntent(coerceText(ctx['applicantType'] ?? '')),
          applicantStatus: 'existing',
          ...extra,
        },
      };
    }

    const detailAction = this.detailActionForFormNode(nodeKey);
    if (detailAction) {
      const id =
        this.readOpportunityIdFromContext(ctx) ||
        coerceText(extra.opportunityId ?? extra.id ?? '').trim();
      if (!id) return null;
      return {
        view: 'detail',
        params: {
          opportunityId: id,
          id,
          applicantType: normalizeTypeIntent(coerceText(ctx['applicantType'] ?? '')),
          wfStep: nodeKey,
          detailTab: extra.detailTab ?? 'lead-details',
          action: detailAction,
          ...extra,
        },
      };
    }

    const mode = this.formNodeMode(nodeKey);
    if (this.isViewNode(nodeKey) || mode === 'view') {
      const id =
        this.readOpportunityIdFromContext(ctx) ||
        coerceText(extra.opportunityId ?? extra.id ?? '').trim();
      if (!id) return null;
      return {
        view: 'detail',
        params: {
          opportunityId: id,
          id,
          applicantType: normalizeTypeIntent(coerceText(ctx['applicantType'] ?? '')),
          wfStep: nodeKey,
          detailTab: extra.detailTab ?? 'lead-details',
          ...extra,
        },
      };
    }

    if (this.isEditFormPauseNode(nodeKey)) {
      const id =
        this.readOpportunityIdFromContext(ctx) ||
        coerceText(extra.opportunityId ?? extra.id ?? '').trim();
      if (!id) return null;
      return {
        view: 'create',
        params: {
          id,
          opportunityId: id,
          wfStep: nodeKey,
          applicantType: normalizeTypeIntent(
            coerceText(ctx['applicantType'] ?? extra.applicantType ?? ''),
          ),
          ...extra,
        },
      };
    }

    if (this.isCreateFormPauseNode(nodeKey)) {
      return {
        view: 'create',
        params: {
          wfStep: nodeKey,
          applicantType: normalizeTypeIntent(coerceText(ctx['applicantType'] ?? '')),
          applicantStatus: this.readApplicantStatusFromContext(ctx),
          ...extra,
        },
      };
    }

    return null;
  }

  isCreateModalNode(nodeKey: string | null | undefined): boolean {
    return (
      this.isApplicantTypeNode(nodeKey) ||
      !!this.isStatusNode(nodeKey) ||
      this.isSearchNode(nodeKey)
    );
  }

  outgoingEdges(nodeKey: string | null | undefined): CanonicalEdge[] {
    if (!nodeKey) return [];
    return this.outgoingByKey.get(nodeKey) ?? [];
  }

  listActionHandle(actionKey: string): string | undefined {
    return resolveListActionHandle(
      actionKey,
      this.dataset()?.actions ?? [],
      this.outgoingEdges(this.roles.listNodeKey),
    );
  }

  ifElseApplicantFieldCode(): string | null {
    return this.roles.ifElseFieldCode;
  }

  /** Workflow key resolved from manifest.json (e.g. OPPORTUNITY → workflows/OPPORTUNITY-v3.json). */
  workflowKey(): string {
    return this.resolvedWorkflowKey ?? 'OPPORTUNITY';
  }

  getNode(nodeKey: string): CanonicalNode | undefined {
    return this.nodesByKey.get(nodeKey);
  }

  private rebuildWorkflowIndexes(): void {
    this.nodesByKey.clear();
    this.outgoingByKey.clear();
    if (!this.workflow) {
      return;
    }
    for (const node of this.workflow.nodes) {
      this.nodesByKey.set(node.nodeKey, node);
    }
    for (const edge of this.workflow.edges) {
      const list = this.outgoingByKey.get(edge.sourceNodeKey) ?? [];
      list.push(edge);
      this.outgoingByKey.set(edge.sourceNodeKey, list);
    }
  }

  async getFormForNode(nodeKey: string): Promise<FormDocument | undefined> {
    await this.ensureLoaded();
    const node = this.getNode(nodeKey);
    const formId = node?.configuration?.['formId'];
    if (typeof formId !== 'string') return undefined;
    const form = await this.enrichEmptyForm(
      await this.pack.getFormById(formId),
      formId,
    );
    return this.normalizeFormDocument(form);
  }

  /**
   * Sort sections/fields by `order`, drop hidden fields, and omit empty sections
   * so pack forms render consistently from workflow-config JSON.
   */
  normalizeFormDocument(form: FormDocument | undefined): FormDocument | undefined {
    const normalized = normalizePageFormDocument(form ?? null) ?? form;
    if (!normalized) return undefined;

    const sectionsInput = normalized.sections;
    if (!sectionsInput?.length) return normalized;

    const catalog = buildFieldCatalogMap(this.pack.getPack()?.fields);

    const sections = this.sortByOrder(sectionsInput, (section, index) =>
      this.readOrder(section['order'], index),
    )
      .map((section) => {
        const elements = Array.isArray(section['elements'])
          ? this.sortByOrder(section['elements'] as unknown[], (el, index) =>
              this.readOrder((el as { order?: number })['order'], index),
            )
          : [];
        return {
          ...section,
          elements,
          fields: this.normalizeFields(section.fields ?? [], catalog),
        };
      })
      .filter(
        (section) =>
          (section.fields?.length ?? 0) > 0 ||
          (Array.isArray(section['elements']) && section['elements'].length > 0),
      );

    const formCode =
      normalized.formCode ??
      coerceText(normalized['code']) ??
      form?.formCode ??
      'UNKNOWN_FORM';

    return { ...normalized, formCode, sections };
  }

  private normalizeFields(
    fields: FormFieldDef[],
    catalog: Map<string, import('../components/workflow-pack-form/pack-field-catalog').FieldCatalogItem>,
  ): FormFieldDef[] {
    return this.sortByOrder(fields, (field, index) =>
      this.readOrder(field['order'], index),
    )
      .map((field) => enrichFieldFromCatalog(field, catalog))
      .filter((field) => {
        const visibility = coerceText(field['visibility'] ?? 'VISIBLE').toUpperCase();
        return visibility !== 'HIDDEN' && visibility !== 'NONE';
      });
  }

  private sortByOrder<T>(
    items: T[],
    getOrder: (item: T, index: number) => number,
  ): T[] {
    return [...items]
      .map((item, index) => ({ item, index }))
      .sort((a, b) => {
        const aOrder = getOrder(a.item, a.index);
        const bOrder = getOrder(b.item, b.index);
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.index - b.index;
      })
      .map(({ item }) => item);
  }

  private readOrder(order: unknown, fallbackIndex: number): number {
    const parsed = Number(order);
    return Number.isFinite(parsed) ? parsed : fallbackIndex;
  }

  /** Map workflow dataset columns → shared Table columns (keeps current Table UX). */
  buildTableColumns(
    onAction: (actionKey: string, row: Record<string, unknown>) => void,
    isActionVisible?: (actionKey: string, row: Record<string, unknown>) => boolean,
    isActionDisabled?: (actionKey: string, row: Record<string, unknown>) => boolean,
  ): TableColumn<Record<string, unknown>>[] {
    const cfg = this.dataset();
    if (!cfg) return [];

    const cols: TableColumn<Record<string, unknown>>[] = cfg.columns
      .filter((c) => c.visible !== false)
      .map((c) => presentOpportunityListColumn(c))
      .filter((c): c is TableColumn<Record<string, unknown>> => c != null);

    // Deduplicate contact/name composites.
    const seen = new Set<string>();
    const deduped = cols.filter((c) => {
      if (seen.has(c.key)) return false;
      seen.add(c.key);
      return true;
    });

    const rowActions = (cfg.actions || []).filter(
      (a) => a.scope === 'row' && !isOmittedOpportunityListRowAction(a, cfg.actions),
    );
    if (rowActions.length) {
      deduped.push({
        key: 'action',
        label: 'ACTION',
        type: 'dropdown',
        align: 'center',
        width: '80px',
        resizable: false,
        dropdownActions: rowActions.map((a) => ({
          label: a.label,
          action: (row: Record<string, unknown>) => onAction(a.key, row),
          getVisible: isActionVisible
            ? (row: Record<string, unknown>) => isActionVisible(a.key, row)
            : undefined,
          getDisabled: isActionDisabled
            ? (row: Record<string, unknown>) => isActionDisabled(a.key, row)
            : undefined,
        })),
      });
    }

    return deduped;
  }

  buildViewTabs(): TabItem[] {
    const view = this.dataset()?.filters.find((f) => f.key === 'view' || f.paramName === 'view');
    const options = view?.optionsConfig?.options;
    if (!options?.length) {
      return [
        { id: 'all', label: 'All' },
        { id: 'draft', label: 'Draft' },
      ];
    }
    return options.map((o) => ({
      id: String(o.value).toLowerCase() === 'draft' ? 'draft' : String(o.value).toLowerCase(),
      label: o.label,
    }));
  }

  buildFilterOptions(filterKey: string): DropdownOption[] {
    const filter = this.dataset()?.filters.find(
      (f) => f.key === filterKey || f.paramName === filterKey,
    );
    const options = filter?.optionsConfig?.options ?? [];
    return [
      { label: `All ${filter?.label ?? filterKey}`, value: '' },
      ...options.map((o) => ({ label: o.label, value: o.value })),
    ];
  }

  toolbarCreateLabel(): string {
    const fromToolbar = this.dataset()?.general?.toolbar?.createButtonLabel;
    if (fromToolbar) return fromToolbar.replace(/^\+\s*/, '');
    const toolbarAction = this.dataset()?.actions.find((a) => a.scope === 'toolbar');
    if (toolbarAction?.label) return toolbarAction.label.replace(/^\+\s*/, '');
    const createAction = this.dataset()?.actions.find((a) =>
      a.label.toLowerCase().includes('create'),
    );
    return createAction?.label?.replace(/^\+\s*/, '') ?? 'Create New Opportunity';
  }

  /** Row actions declared on dataset_opportunity in the active workflow JSON. */
  listRowActions(): DatasetActionConfig[] {
    return (this.dataset()?.actions ?? []).filter((a) => a.scope === 'row');
  }

  /** Toolbar actions declared on dataset_opportunity in the active workflow JSON. */
  listToolbarActions(): DatasetActionConfig[] {
    return (this.dataset()?.actions ?? []).filter((a) => a.scope === 'toolbar');
  }

  /**
   * Build workflow resume payload for a dataset row action, honouring each action's
   * `config.fieldMapping` from the workflow JSON (same shape as wf-dataset-panel).
   */
  buildListActionOutput(
    actionKey: string,
    row: Record<string, unknown>,
  ): Record<string, unknown> {
    const action = this.dataset()?.actions.find((a) => a.key === actionKey);
    const id = coerceText(row['id'] ?? row['opportunityId'] ?? '');
    const output: Record<string, unknown> = {
      actionKey,
      id,
      opportunityId: id,
      selectedRow: row,
    };

    const mapping =
      (action?.config?.['fieldMapping'] as Record<string, string> | undefined) ?? {};
    for (const [target, source] of Object.entries(mapping)) {
      let value = row[source];
      if (
        (value === undefined || value === null || value === '') &&
        source === 'column'
      ) {
        value = row['id'] ?? row['opportunityId'];
      }
      if (value !== undefined && value !== null && value !== '') {
        output[target] = value;
      }
    }

    const mappedOpportunityId = coerceText(
      output['opportunityId'] ?? output['opportunityID'] ?? '',
    );
    if (mappedOpportunityId) {
      output['opportunityId'] = mappedOpportunityId;
      output['id'] = mappedOpportunityId;
    }

    const applicantType = normalizeApplicantTypeForWorkflow(
      output['applicantType'] ?? row['applicantType'],
    );
    if (applicantType) {
      output['applicantType'] = applicantType;
      const ifElseField = this.ifElseApplicantFieldCode();
      if (ifElseField) {
        // v11 Edit/View if-else compares `column13` to `corporate` | `individual`.
        output[ifElseField] = applicantType.toLowerCase();
      }
    }

    for (const [key, value] of Object.entries(row)) {
      if (value !== null && typeof value !== 'object' && output[key] === undefined) {
        output[key] = value;
      }
    }

    return output;
  }

  /**
   * Full dataset_opportunity node configuration for wf-dataset-panel.
   * Rewrites pack placeholder endpoints to the real LOS `/opportunities` API
   * and normalizes responseMapping for the backend envelope.
   */
  runtimeDatasetPanelConfig(): Record<string, unknown> | null {
    const node = this.getNode(this.roles.listNodeKey ?? 'dataset_opportunity');
    const configuration = node?.configuration;
    if (!configuration) return null;

    const config = structuredClone(configuration);
    const ds = {
      ...((config['dataSource'] as Record<string, unknown>) ?? {}),
    };
    const endpoint = typeof ds['endpoint'] === 'string' ? ds['endpoint'] : '';
    // Pack ships with https://api.example.com/opportunities — run against LOS API.
    if (!endpoint || /api\.example\.com/i.test(endpoint) || /\/opportunities/i.test(endpoint)) {
      ds['endpoint'] = '/opportunities';
    }
    ds['method'] = typeof ds['method'] === 'string' ? ds['method'] : 'GET';
    ds['responseMapping'] = {
      collectionPath: 'data',
      totalPath: 'metadata.total',
    };
    delete ds['credentialId'];
    delete ds['auth'];
    delete ds['headers'];
    config['dataSource'] = ds;

    // Host page owns title + Create CTA; panel keeps search/refresh + row actions.
    const actions = Array.isArray(config['actions'])
      ? (config['actions'] as Array<{ scope?: string }>).filter((a) => a.scope !== 'toolbar')
      : [];
    config['actions'] = actions;
    const general = {
      ...((config['general'] as Record<string, unknown>) ?? {}),
    };
    delete general['title'];
    delete general['description'];
    config['general'] = general;
    return config;
  }

  pageTitle(): string {
    return this.dataset()?.general.title ?? 'Opportunity';
  }

  pageSubtitle(): string {
    return this.dataset()?.general.description ?? 'Manage leads & opportunities';
  }

  defaultPageSize(): number {
    return this.dataset()?.general.defaultPageSize ?? 10;
  }

  /** Form section titles for create sidebar (config-driven labels). */
  formSectionTitles(form: FormDocument | undefined): string[] {
    return (form?.sections ?? []).map(
      (s: FormSectionDef, i: number) =>
        (typeof s['title'] === 'string' && s['title']) ||
        (typeof s.label === 'string' && s.label) ||
        `Section ${i + 1}`,
    );
  }

  formFields(form: FormDocument | undefined): FormFieldDef[] {
    return (form?.sections ?? []).flatMap((s) => s.fields ?? []);
  }

  /** Workflow node key for the create form matching applicant type + status. */
  createFormNodeKey(
    applicantType: string | null | undefined,
    applicantStatus: string | null | undefined,
  ): string {
    const corporate = normalizeTypeIntent(coerceText(applicantType)) === 'corporate';
    const existing = normalizeStatusIntent(coerceText(applicantStatus)) === 'existing';

    if (existing) {
      const fromDataset = this.outgoingEdges(this.customerDatasetNodeKey(applicantType))
        .map((edge) => edge.targetNodeKey)
        .find((key) => key && this.isCreateExistingFormNode(key));
      if (fromDataset) {
        return fromDataset;
      }
      if (corporate) return 'form_create_corporate_existing';
      return 'form_create_individual_existing';
    }

    if (corporate) return 'form_create_corporate';
    return 'form_create_individual';
  }

  /** Any create-existing form node — used to bootstrap GET /parties/:id before type is known. */
  bootstrapExistingCreateFormNodeKey(): string {
    for (const applicantType of ['individual', 'corporate'] as const) {
      const nodeKey = this.outgoingEdges(this.customerDatasetNodeKey(applicantType))
        .map((edge) => edge.targetNodeKey)
        .find((key) => key && this.isCreateExistingFormNode(key));
      if (nodeKey) {
        return nodeKey;
      }
    }
    return 'forms.load-form-24';
  }

  /** Create-existing node that loads party details before prefill (`forms.load-form-*`). */
  isCreateExistingFormNode(nodeKey: string | null | undefined): boolean {
    if (!nodeKey) return false;
    const node = this.getNode(nodeKey);
    if (node && isCreateExistingPartyFormNode(node)) {
      return true;
    }
    if (nodeKey.startsWith('form_create_') && nodeKey.includes('existing')) return true;
    return /\/parties\/\{\{customerId\}\}/i.test(this.readNodeDataSourceEndpoint(nodeKey));
  }

  private isPartyDetailDataSource(dataSource: Record<string, unknown>): boolean {
    const endpoint = coerceText(dataSource['endpoint'] ?? '');
    return /\/parties\/(?:\{\{customerId\}\}|[^/?]+)/i.test(endpoint);
  }

  searchFormNodeKey(applicantType: string | null | undefined): string {
    return String(applicantType ?? '').toLowerCase() === 'corporate'
      ? 'form_search_corporate'
      : 'form_search_individual';
  }

  customerDatasetNodeKey(applicantType: string | null | undefined): string {
    const searchKey = this.searchFormNodeKey(applicantType);
    const datasetTarget = this.outgoingEdges(searchKey).find((edge) =>
      this.isCustomerDatasetNode(edge.targetNodeKey),
    )?.targetNodeKey;
    if (datasetTarget) {
      return datasetTarget;
    }

    return String(applicantType ?? '').toLowerCase() === 'corporate'
      ? 'dataset_customers_corporate'
      : 'dataset_customers_individual';
  }

  /** Dataset node that loads existing-customer search results (legacy or designer keys). */
  isCustomerDatasetNode(nodeKey: string | null | undefined): boolean {
    if (!nodeKey) return false;
    if (nodeKey.startsWith('dataset_customers_')) return true;
    return /fetch-existing-customer/i.test(this.readNodeDataSourceEndpoint(nodeKey));
  }

  private readNodeDataSourceEndpoint(nodeKey: string): string {
    const configuration = this.getNode(nodeKey)?.configuration;
    if (!configuration || typeof configuration !== 'object' || Array.isArray(configuration)) {
      return '';
    }
    const dataSource = configuration['dataSource'];
    if (!dataSource || typeof dataSource !== 'object' || Array.isArray(dataSource)) {
      return '';
    }
    return coerceText((dataSource as Record<string, unknown>)['endpoint'] ?? '');
  }

  /** Existing-customer search modal fields from pack forms (manifest-driven). */
  async searchFields(applicantType: string | null | undefined): Promise<SearchFieldUi[]> {
    await this.ensureLoaded();
    const form = await this.getFormForNode(this.searchFormNodeKey(applicantType));
    const fields = form?.sections?.[0]?.fields ?? [];

    return fields.map((field) => {
      const fieldType = String(field?.fieldType ?? '').toUpperCase();
      return {
        fieldCode: field.fieldCode,
        label: typeof field.label === 'string' ? field.label : field.fieldCode,
        placeholder:
          typeof field.placeholder === 'string' ? field.placeholder : undefined,
        control: this.searchFieldControl(fieldType),
      };
    });
  }

  private searchFieldControl(fieldType: string): SearchFieldUi['control'] {
    if (fieldType === 'DATE') return 'date';
    if (fieldType === 'PHONE') return 'phone';
    return 'text';
  }

  /** Customer results page chrome from dataset_customers_* nodes. */
  customerSearchUi(applicantType: string | null | undefined): CustomerSearchUiConfig {
    const node = this.getNode(this.customerDatasetNodeKey(applicantType));
    const c = node?.configuration ?? {};
    const general = (c['general'] as Record<string, unknown>) ?? {};
    const appearance = (c['appearance'] as Record<string, unknown>) ?? {};
    const actions = (c['actions'] as DatasetActionConfig[]) ?? [];
    const createAction =
      actions.find((a) => a.key === 'create_opportunity') ??
      actions.find((a) => a.scope === 'row');
    return {
      title:
        (typeof general['title'] === 'string' && general['title']) ||
        'Existing Customer - Search Results',
      description:
        (typeof general['description'] === 'string' && general['description']) ||
        'Results found based on provided search criteria',
      createActionLabel:
        createAction?.label?.replace(/^\+\s*/, '') ?? 'Create New Opportunity',
      emptyStateText:
        (typeof appearance['emptyStateText'] === 'string' && appearance['emptyStateText']) ||
        'No results found',
    };
  }

  /** Raw pack dataSource for a dataset node (used by PackDataSourceService). */
  datasetDataSource(nodeKey: string): Record<string, unknown> | null {
    const ds = this.getNode(nodeKey)?.configuration?.['dataSource'];
    if (!ds || typeof ds !== 'object') return null;
    return structuredClone(ds) as Record<string, unknown>;
  }

  opportunityListDataSource(): Record<string, unknown> | null {
    const ds = this.datasetDataSource(this.roles.listNodeKey ?? 'dataset_opportunity');
    if (!ds) return null;
    const endpoint = coerceText(ds['endpoint'] ?? '');
    if (/fetch-existing-customer/i.test(endpoint)) {
      return null;
    }
    return this.normalizeOpportunityListDataSource(ds);
  }

  /**
   * Normalize pack list dataSource for the admin LOS runtime:
   * strip credentials and map external opportunity URLs to `/opportunities`.
   */
  private normalizeOpportunityListDataSource(
    dataSource: Record<string, unknown>,
  ): Record<string, unknown> {
    const normalized = structuredClone(dataSource);
    delete normalized['credentialId'];
    delete normalized['auth'];
    delete normalized['headers'];

    this.rewriteOpportunityListEndpoint(normalized);
    this.applyListResponseMappingDefaults(normalized);
    normalized['query'] = this.normalizeOpportunityListQuery(normalized['query']);

    return normalized;
  }

  private applyListResponseMappingDefaults(normalized: Record<string, unknown>): void {
    const rawMapping = normalized['responseMapping'];
    const mapping =
      rawMapping && typeof rawMapping === 'object' && !Array.isArray(rawMapping)
        ? (rawMapping as Record<string, unknown>)
        : {};

    normalized['responseMapping'] = {
      ...mapping,
      collectionPath: mapping['collectionPath'] ?? 'data',
      totalPath: mapping['totalPath'] ?? 'metadata.total',
    };
  }

  private rewriteOpportunityListEndpoint(normalized: Record<string, unknown>): void {
    const endpoint = coerceText(normalized['endpoint'] ?? '');
    if (!endpoint) {
      return;
    }
    if (/api\.example\.com/i.test(endpoint)) {
      normalized['endpoint'] = '/opportunities';
      return;
    }
    if (!/^https?:\/\//i.test(endpoint)) {
      return;
    }
    try {
      const url = new URL(endpoint);
      if (/\/opportunities\/?$/i.test(url.pathname)) {
        normalized['endpoint'] = '/opportunities';
      }
    } catch {
      normalized['endpoint'] = '/opportunities';
    }
  }

  private normalizeOpportunityListQuery(
    query: unknown,
  ): Array<{ key?: string; value?: string }> {
    const queryItems = this.mapOpportunityListQueryItems(query);
    const present = new Set(
      queryItems.map((item) => coerceText(item.key)).filter(Boolean),
    );
    for (const extra of [
      { key: 'search', value: '{{search}}' },
      { key: 'priorityCode', value: '{{priorityCode}}' },
      { key: 'status', value: '{{status}}' },
      { key: 'view', value: '{{view}}' },
      { key: 'createdFrom', value: '{{createdFrom}}' },
      { key: 'createdTo', value: '{{createdTo}}' },
    ]) {
      if (!present.has(extra.key)) {
        queryItems.push(extra);
      }
    }
    return queryItems;
  }

  private mapOpportunityListQueryItems(
    query: unknown,
  ): Array<{ key?: string; value?: string }> {
    if (!Array.isArray(query)) {
      return [];
    }
    return (query as Array<{ key?: string; value?: string }>).flatMap((item) => {
      if (item.key === 'dateRange') {
        return [
          { key: 'createdFrom', value: '{{createdFrom}}' },
          { key: 'createdTo', value: '{{createdTo}}' },
        ];
      }
      if (item.key === 'pageSize') {
        return [{ key: 'limit', value: item.value ?? '{{pageSize}}' }];
      }
      if (item.key === 'priority') {
        return [{ key: 'priorityCode', value: item.value ?? '{{priorityCode}}' }];
      }
      return [item];
    });
  }

  customerListDataSource(applicantType: string | null | undefined): Record<string, unknown> | null {
    const ds = this.datasetDataSource(this.customerDatasetNodeKey(applicantType));
    if (!ds) return null;
    return this.normalizeCustomerSearchDataSource(ds);
  }

  /**
   * GET dataSource for opportunity detail — prefers `detail_opportunity` (v3),
   * then `form_view_*` nodes from the active workflow JSON (v5+).
   */
  resolveViewFormNodeKey(
    applicantType?: string | null,
    pausedNodeKey?: string | null,
  ): string {
    if (pausedNodeKey && this.isViewNode(pausedNodeKey)) {
      return pausedNodeKey;
    }
    return this.viewFormNodeKey(applicantType);
  }

  resolveDetailDataSource(
    opportunityId: string,
    applicantType?: string | null,
    pausedNodeKey?: string | null,
  ): Record<string, unknown> | null {
    const fromDetailNode = this.detailDataSource(opportunityId);
    if (fromDetailNode) return fromDetailNode;

    const viewNodeKey = this.resolveViewFormNodeKey(applicantType, pausedNodeKey);
    const fromViewNode = this.formNodeDataSource(viewNodeKey, { opportunityId });
    if (fromViewNode) return fromViewNode;

    return {
      endpoint: `/opportunities/${opportunityId}`,
      method: 'GET',
      responseMapping: { recordPath: 'data' },
    };
  }

  /**
   * GET dataSource for opportunity edit — reads `form_edit_*` node configuration.
   */
  resolveEditDataSource(
    opportunityId: string,
    applicantType?: string | null,
  ): Record<string, unknown> | null {
    const editNodeKey = this.editFormNodeKey(applicantType);
    const fromEditNode = this.formNodeDataSource(editNodeKey, { opportunityId });
    if (fromEditNode && !this.isPartyDetailDataSource(fromEditNode)) {
      return fromEditNode;
    }

    // v11 form_edit_* nodes have no GET dataSource; load the opportunity record.
    return {
      endpoint: `/opportunities/${opportunityId}`,
      method: 'GET',
      responseMapping: { recordPath: 'data' },
    };
  }

  /** Normalized dataSource from any `forms.render-form` workflow node. */
  formNodeDataSource(
    nodeKey: string,
    vars: PackDataSourceVars = {},
  ): Record<string, unknown> | null {
    const raw = this.getNode(nodeKey)?.configuration?.['dataSource'];
    if (!raw || typeof raw !== 'object') return null;
    return this.normalizeFormRecordDataSource(raw as Record<string, unknown>, vars);
  }

  viewFormNodeKey(applicantType?: string | null): string {
    return this.pickFormNodeByApplicantType(
      this.roles.viewNodeKeys,
      applicantType,
      'form_view_individual',
      'form_view_corporate',
    );
  }

  editFormNodeKey(applicantType?: string | null): string {
    return this.pickFormNodeByApplicantType(
      this.roles.editNodeKeys,
      applicantType,
      'form_edit_individual',
      'form_edit_corporate',
    );
  }

  private pickFormNodeByApplicantType(
    nodeKeys: string[],
    applicantType: string | null | undefined,
    legacyIndividualKey: string,
    legacyCorporateKey: string,
  ): string {
    const isCorporate = uiApplicantType(applicantType) === 'corporate';
    const fallback = isCorporate ? legacyCorporateKey : legacyIndividualKey;
    if (!nodeKeys.length) {
      return fallback;
    }

    const byKind = nodeKeys.find((key) => {
      const node = this.getNode(key);
      const blob = `${key} ${node?.label ?? ''}`.toLowerCase();
      return isCorporate ? blob.includes('corporate') : blob.includes('individual');
    });
    if (byKind) {
      return byKind;
    }

    const first = nodeKeys[0];
    const last = nodeKeys.at(-1);
    if (nodeKeys.length === 1 && first) {
      return first;
    }
    if (isCorporate && last) {
      return last;
    }
    return first ?? fallback;
  }

  private readNodeResponseFieldMapping(nodeKey: string): Record<string, string> {
    const mapping = this.getNode(nodeKey)?.configuration?.['responseFieldMapping'];
    if (!mapping || typeof mapping !== 'object' || Array.isArray(mapping)) {
      return {};
    }
    return mapping as Record<string, string>;
  }

  private normalizeFormRecordDataSource(
    dataSource: Record<string, unknown>,
    vars: PackDataSourceVars,
  ): Record<string, unknown> {
    const normalized = structuredClone(dataSource);
    delete normalized['credentialId'];
    delete normalized['auth'];
    delete normalized['headers'];

    let endpoint = coerceText(normalized['endpoint'] ?? '');
    for (const [key, value] of Object.entries(vars)) {
      endpoint = endpoint.replace(
        new RegExp(`\\{\\{${key}\\}\\}`, 'g'),
        value != null ? String(value) : '',
      );
    }

    if (!endpoint || /api\.example\.com/i.test(endpoint)) {
      const id = vars['opportunityId'];
      if (id) {
        normalized['endpoint'] = `/opportunities/${id}`;
      }
    } else {
      normalized['endpoint'] = resolvePackEndpoint(endpoint);
    }

    const mapping =
      normalized['responseMapping'] && typeof normalized['responseMapping'] === 'object'
        ? (normalized['responseMapping'] as Record<string, unknown>)
        : {};
    normalized['responseMapping'] = {
      ...mapping,
      recordPath: mapping['recordPath'] ?? 'data',
    };

    return normalized;
  }

  private normalizeCustomerSearchDataSource(
    dataSource: Record<string, unknown>,
  ): Record<string, unknown> {
    const normalized = structuredClone(dataSource);
    delete normalized['credentialId'];
    delete normalized['auth'];
    delete normalized['headers'];

    const endpoint = coerceText(normalized['endpoint'] ?? '');
    if (!endpoint || /api\.example\.com/i.test(endpoint)) {
      normalized['endpoint'] = '/parties';
      normalized['method'] = 'GET';
      return normalized;
    }

    if (/\/fetch-existing-customer\/?$/i.test(endpoint)) {
      normalized['endpoint'] = '/common/fetch-existing-customer';
      normalized['method'] = 'GET';
      const body = normalized['body'];
      if (body && typeof body === 'object' && !Array.isArray(body)) {
        normalized['query'] = Object.entries(body as Record<string, unknown>).map(
          ([key, value]) => ({
            key,
            value: coerceText(value ?? ''),
          }),
        );
        delete normalized['body'];
      }
    }

    return normalized;
  }

  /** Filters for the list drawer (excludes view tabs and date-range calendar). */
  listFiltersForUi(): DatasetFilterConfig[] {
    const declared = (this.dataset()?.filters ?? []).filter(
      (f) => f.key !== 'view' && f.control !== 'dateRange' && f.key !== 'dateRange',
    );
    if (declared.length) {
      return declared;
    }
    return this.listFiltersFromColumns();
  }

  private listFiltersFromColumns(): DatasetFilterConfig[] {
    const columns = this.dataset()?.columns ?? [];
    return columns
      .filter((column) => column.filter === true && isOpportunityListDrawerFilter(column))
      .map((column) => {
        const paramName = listFilterParamName(column);
        return {
          key: paramName,
          label: formatListFilterLabel(column.label),
          control: 'select',
          paramName,
          optionsConfig: {
            options: column.filterOptions ?? [],
            source: 'static',
          },
        };
      });
  }

  listSearchEnabled(): boolean {
    const general = this.dataset()?.general.searchEnabled;
    if (general === false) {
      return false;
    }
    return (this.dataset()?.columns ?? []).some((column) => column.search === true) || general === true;
  }

  listSearchPlaceholder(): string {
    const labels = (this.dataset()?.columns ?? [])
      .filter((column) => column.search === true)
      .map((column) => formatListFilterLabel(column.label));
    if (!labels.length) {
      return 'Search';
    }
    return `Search ${labels.join(', ')}`;
  }

  dateRangeFilter(): DatasetFilterConfig | undefined {
    return this.dataset()?.filters.find(
      (f) => f.control === 'dateRange' || f.key === 'dateRange',
    );
  }

  /** Map LOS API rows onto workflow dataset column sourcePaths. */
  normalizeListRow(row: Record<string, unknown>): Record<string, unknown> {
    const createdAt = row['createdAt'];
    const rawStatus = coerceText(row['status'] ?? '');
    return {
      ...row,
      statusRaw: rawStatus,
      id: row['id'] ?? row['opportunityId'],
      opportunityId: row['opportunityId'] ?? row['id'],
      opportunityNumber:
        row['opportunityNumber'] ??
        row['opportunity_number'] ??
        '—',
      name: row['name'] ?? row['organizationName'] ?? row['fullName'] ?? '—',
      email: row['email'] ?? '—',
      phone: row['phone'] ?? row['mobileMasked'] ?? row['mobile'] ?? '—',
      location: row['location'] ?? row['preferredBranchLabel'] ?? '—',
      product: row['product'] ?? row['productLabel'] ?? row['productId'] ?? '—',
      generatedDate:
        row['generatedDate'] ??
        (typeof createdAt === 'string' ? formatPackListDate(createdAt) : '—'),
      aiScore: row['aiScore'] ?? row['opportunityScore'] ?? 0,
      source: formatPackListLabel(
        coerceText(row['source'] ?? row['sourceTypeLabel'] ?? row['sourceTypeCode'] ?? '—'),
      ),
      priority: formatPackListLabel(
        coerceText(row['priority'] ?? row['priorityLabel'] ?? row['priorityCode'] ?? '—'),
      ),
      status: formatPackListStatus(rawStatus || '—'),
      applicantType: row['applicantType'] ?? row['applicant_type'] ?? '',
    };
  }

  buildListQueryVars(input: {
    page: number;
    pageSize: number;
    search?: string;
    activeTabId: string;
    tabItems: TabItem[];
    filterValues: Record<string, string>;
    createdFrom?: string;
    createdTo?: string;
  }): PackDataSourceVars {
    const vars: PackDataSourceVars = {
      page: input.page,
      pageSize: input.pageSize,
    };

    const search = input.search?.trim();
    if (search) {
      vars['search'] = search;
    }

    const viewFilter = this.dataset()?.filters.find((f) => f.key === 'view');
    const viewParam = viewFilter?.paramName ?? 'view';
    if (this.isDraftTabId(input.activeTabId)) {
      vars[viewParam] = 'Draft';
    } else if (input.activeTabId === 'all') {
      vars[viewParam] = 'All';
    } else {
      const tab = input.tabItems.find((t) => t.id === input.activeTabId);
      vars[viewParam] = tab?.label ?? 'All';
    }

    for (const filter of this.listFiltersForUi()) {
      const raw = input.filterValues[filter.key]?.trim();
      if (!raw) {
        continue;
      }
      const paramName = filter.paramName ?? filter.key;
      vars[paramName] = normalizeListFilterQueryValue(paramName, raw);
    }

    if (input.createdFrom) {
      vars['createdFrom'] = input.createdFrom;
    }
    if (input.createdTo) {
      vars['createdTo'] = input.createdTo;
    }

    return vars;
  }

  private isDraftTabId(tabId: string): boolean {
    return tabId === 'draft' || tabId === 'darft';
  }

  detailDataSource(opportunityId: string): Record<string, unknown> | null {
    const node = this.getNode('detail_opportunity');
    const raw = node?.configuration?.['dataSource'];
    if (!raw || typeof raw !== 'object') return null;

    const normalized = structuredClone(raw) as Record<string, unknown>;
    delete normalized['credentialId'];
    delete normalized['auth'];
    delete normalized['headers'];

    const endpoint = coerceText(normalized['endpoint'] ?? '');
    if (!endpoint || /api\.example\.com/i.test(endpoint)) {
      normalized['endpoint'] = `/opportunities/${opportunityId}`;
    } else {
      normalized['endpoint'] = resolvePackEndpoint(
        endpoint.replace(/\{\{opportunityId\}\}/g, opportunityId),
      );
    }

    const mapping =
      normalized['responseMapping'] && typeof normalized['responseMapping'] === 'object'
        ? (normalized['responseMapping'] as Record<string, unknown>)
        : {};
    normalized['responseMapping'] = {
      ...mapping,
      recordPath: mapping['recordPath'] ?? 'data',
    };

    return normalized;
  }

  async detailHeaderSummary(
    applicantType?: string | null,
  ): Promise<DetailHeaderSummaryItem[]> {
    const header = this.recordDetail()?.header;
    const fromRecord = (header?.summary ?? []).map((item) => ({
      key: item.key,
      label: item.label,
      sourcePath: item.sourcePath,
      dataType: item.dataType,
    }));
    if (fromRecord.length) {
      return fromRecord;
    }
    return this.buildViewWorkflowHeaderSummary(applicantType);
  }

  private async buildViewWorkflowHeaderSummary(
    applicantType?: string | null,
  ): Promise<DetailHeaderSummaryItem[]> {
    await this.ensureLoaded();
    const nodeKey = this.viewFormNodeKey(applicantType);
    const form = await this.getFormForNode(nodeKey);
    const responseFieldMapping = await this.viewResponseFieldMapping(applicantType);
    return buildDetailHeaderSummaryFromViewForm({ form, responseFieldMapping });
  }

  detailMilestoneTitle(): string {
    return this.recordDetail()?.milestones?.title ?? 'Application Milestone';
  }

  detailBackLabel(): string {
    return this.recordDetail()?.general?.backLabel ?? 'Back to Opportunities';
  }

  detailIconActions(): RecordDetailUiConfig['actions'] {
    const fromRecord = (this.recordDetail()?.actions ?? []).filter(
      (action) => action.scope === 'icon',
    );
    if (fromRecord.length) {
      return fromRecord;
    }
    return this.resolveDetailIconActionsFromWorkflow();
  }

  detailButtonActions(): RecordDetailUiConfig['actions'] {
    const fromRecord = (this.recordDetail()?.actions ?? []).filter(
      (action) => action.scope === 'primary' || action.scope === 'secondary',
    );
    if (fromRecord.length) {
      return fromRecord;
    }
    return this.resolveDetailButtonActionsFromWorkflow();
  }

  /** All detail-page actions (icons + buttons) with workflow-derived labels. */
  resolvedDetailActions(): RecordDetailUiConfig['actions'] {
    return [...this.detailIconActions(), ...this.detailButtonActions()];
  }

  submitNodeKeyForDetailAction(actionKey: string): string | null {
    const intent = opportunityActionIntent(actionKey, this.listRowActions());
    const normalized = normalizeWorkflowDetailActionKey(actionKey);
    if (intent === 'assign' || normalized === 'assign_to_rm') {
      return 'submit_assign_rm';
    }
    if (intent === 'follow_up' || normalized === 'follow_up') {
      return 'submit_follow_up';
    }
    if (intent === 'reject' || normalized === 'reject_opportunity') {
      return 'submit_reject_opportunity';
    }
    return null;
  }

  private resolveDetailIconActionsFromWorkflow(): RecordDetailUiConfig['actions'] {
    const icons: RecordDetailUiConfig['actions'] = [
      { key: 'call', label: 'Call', scope: 'icon', icon: 'Phone' },
      { key: 'mail', label: 'Mail', scope: 'icon', icon: 'Mail' },
      { key: 'edit', label: 'Edit', scope: 'icon', icon: 'Pencil' },
    ];

    for (const action of this.listRowActions()) {
      if (opportunityActionIntent(action.key, [action]) !== 'follow_up') {
        continue;
      }
      if (!icons.some((item) => item.key === 'follow_up')) {
        icons.push({
          key: 'follow_up',
          label: action.label,
          scope: 'icon',
          icon: 'Bell',
        });
      }
    }

    if (!icons.some((item) => item.key === 'follow_up')) {
      const followNode = this.getNode('form_follow_up');
      if (followNode?.label) {
        icons.push({
          key: 'follow_up',
          label: followNode.label,
          scope: 'icon',
          icon: 'Bell',
        });
      }
    }

    return icons;
  }

  private resolveDetailButtonActionsFromWorkflow(): RecordDetailUiConfig['actions'] {
    const buttons: RecordDetailUiConfig['actions'] = [];

    for (const action of this.listRowActions()) {
      const mapped = this.mapDatasetActionToDetailButton(action);
      if (mapped && !buttons.some((item) => item.key === mapped.key)) {
        buttons.push(mapped);
      }
    }

    this.appendDetailButtonIfMissing(buttons, 'assign_to_rm', 'form_assign_rm');
    this.appendDetailButtonIfMissing(buttons, 'reject_opportunity', 'form_reject_opportunity', true);

    return buttons;
  }

  private mapDatasetActionToDetailButton(
    action: DatasetActionConfig,
  ): RecordDetailUiConfig['actions'][0] | null {
    const intent = opportunityActionIntent(action.key, [action]);
    if (intent === 'assign') {
      return { key: 'assign_to_rm', label: action.label, scope: 'secondary' };
    }
    if (intent === 'reject') {
      return {
        key: 'reject_opportunity',
        label: action.label,
        scope: 'secondary',
        confirm: action.confirm === true,
      };
    }
    if (intent === 'convert') {
      return { key: 'convert_to_application', label: action.label, scope: 'primary' };
    }
    return null;
  }

  private appendDetailButtonIfMissing(
    buttons: RecordDetailUiConfig['actions'],
    actionKey: string,
    nodeKey: string,
    confirm = false,
  ): void {
    if (buttons.some((item) => item.key === actionKey)) {
      return;
    }
    const node = this.getNode(nodeKey);
    if (!node?.label) {
      return;
    }
    buttons.push({
      key: actionKey,
      label: node.label,
      scope: 'secondary',
      confirm,
    });
  }

  mapRecordValue(record: Record<string, unknown>, path: string): unknown {
    return getByPath(record, path);
  }

  /** Pack fieldCode → API record path for view/edit form nodes. */
  async viewResponseFieldMapping(
    applicantType?: string | null,
    pausedNodeKey?: string | null,
  ): Promise<Record<string, string>> {
    const nodeKey = this.resolveViewFormNodeKey(applicantType, pausedNodeKey);
    const form = await this.getFormForNode(nodeKey);
    return buildDetailResponseFieldMapping(
      form,
      applicantType,
      this.readNodeResponseFieldMapping(nodeKey),
    );
  }

  /**
   * Detail tab field layout — prefers `detail_opportunity` sections (v3),
   * otherwise derives sections from `form_view_*` form JSON (v5+).
   */
  async resolveDetailTabConfigs(
    applicantType?: string | null,
    pausedNodeKey?: string | null,
  ): Promise<DetailTabConfig[]> {
    await this.ensureLoaded();

    const fromRecordDetail = buildDetailTabsFromRecordDetail(
      this.getNode('detail_opportunity')?.configuration?.['tabs'] as
        | RecordDetailUiConfig['tabs']
        | undefined,
    );
    const nodeKey = this.resolveViewFormNodeKey(applicantType, pausedNodeKey);
    const form = await this.getFormForNode(nodeKey);
    const responseFieldMapping = await this.viewResponseFieldMapping(
      applicantType,
      pausedNodeKey,
    );

    if (fromRecordDetail.length) {
      return finalizeDetailTabConfigs(fromRecordDetail, {
        form,
        responseFieldMapping,
      });
    }

    return buildDetailTabsFromFormView({
      form,
      responseFieldMapping,
    });
  }

  /**
   * Build workflow resume payload for an existing-customer dataset row action,
   * honouring each action's `config.fieldMapping` from the workflow JSON.
   */
  buildCustomerDatasetActionOutput(
    datasetNodeKey: string,
    actionKey: string,
    row: Record<string, unknown>,
    ctx: Record<string, unknown> = {},
  ): Record<string, unknown> {
    const node = this.getNode(datasetNodeKey);
    const actions = (node?.configuration?.['actions'] as DatasetActionConfig[] | undefined) ?? [];
    const action =
      actions.find((item) => item.key === actionKey) ??
      actions.find((item) => item.type === 'openForm' && item.scope === 'row');
    const output: Record<string, unknown> = {
      actionKey: action?.key ?? actionKey,
      selectedRow: row,
      applicantType: normalizeApplicantTypeForWorkflow(
        ctx['applicantType'] ?? ctx['applicantTypes'] ?? row['type'],
      ),
      applicantStatus: 'Existing Customer',
      statusOptions: ctx['statusOptions'] ?? ctx['customerStatus'] ?? 'Existing Customer',
      customerStatus: ctx['customerStatus'] ?? ctx['statusOptions'] ?? 'Existing Customer',
      customerName: coerceText(row['name'] ?? row['customerName'] ?? ''),
    };

    const mapping =
      (action?.config?.['fieldMapping'] as Record<string, string> | undefined) ?? {};
    for (const [target, source] of Object.entries(mapping)) {
      let value = row[source];
      if (
        (value === undefined || value === null || value === '') &&
        source === 'column'
      ) {
        value = row['id'] ?? row['customerId'];
      }
      if (value !== undefined && value !== null && value !== '') {
        output[target] = value;
      }
    }

    if (!output['customerId']) {
      output['customerId'] = row['customerId'] ?? row['id'];
    }
    if (!output['id']) {
      output['id'] = row['id'] ?? output['customerId'];
    }

    for (const [key, value] of Object.entries(row)) {
      if (value !== null && typeof value !== 'object' && output[key] === undefined) {
        output[key] = value;
      }
    }

    return output;
  }

  detailTabByKey(
    tabConfigs: DetailTabConfig[],
    tabKey: string,
  ): DetailTabConfig | undefined {
    return tabConfigs.find((tab) => tab.key === tabKey);
  }

  createPageTitle(): string {
    return this.toolbarCreateLabel() || 'Create New Opportunity';
  }

  createPageSubtitle(): string {
    return 'Provide basic borrower and loan inputs to begin the application process.';
  }

  /** Footer actions for create / edit opportunity form (manifest-driven). */
  createFooterActions(
    applicantType: string | null | undefined,
    applicantStatus: string | null | undefined,
    isEditMode: boolean,
    isDraftOpportunity = false,
  ): { cancelLabel: string; draftLabel: string; submitLabel: string; showDraftButton: boolean } {
    const choice = this.readChoice(this.roles.applicantTypeNodeKey, 'applicantTypes');
    const formNode = this.getNode(
      isEditMode
        ? this.editFormNodeKey(applicantType)
        : this.createFormNodeKey(applicantType, applicantStatus),
    );
    const formConfig = formNode?.configuration ?? {};
    const configuredDraft =
      typeof formConfig['draftLabel'] === 'string' ? formConfig['draftLabel'] : undefined;

    let submitLabel = 'Submit';
    if (isEditMode) {
      submitLabel =
        typeof formConfig['updateLabel'] === 'string'
          ? formConfig['updateLabel']
          : 'Save Changes';
    }

    return {
      cancelLabel: choice?.cancelLabel ?? 'Cancel',
      draftLabel: configuredDraft ?? 'Save Draft',
      submitLabel,
      showDraftButton: !isEditMode || isDraftOpportunity,
    };
  }

  /**
   * Map pack create-form fields onto existing Angular control names.
   * Pack fieldCodes are generic (textField/phone/…); mapping is by section + type/order.
   */
  createFieldLabels(
    form: FormDocument | undefined,
    applicantType: string | null | undefined,
  ): Record<string, FieldLabelUi> {
    const out: Record<string, FieldLabelUi> = {};
    const isCorporate = String(applicantType ?? '').toLowerCase() === 'corporate';
    const sections = form?.sections ?? [];

    const basic = sections[0]?.fields ?? [];
    if (isCorporate) {
      assignField(out, 'organizationName', basic[0]);
      assignField(out, 'contactNumber', basic[1]);
      assignField(out, 'emailId', basic[2]);
      assignField(out, 'dateOfIncorporation', basic[3]);
    } else {
      assignField(out, 'applicantName', basic[0]);
      assignField(out, 'contactNumber', basic[1]);
      assignField(out, 'emailId', basic[2]);
      assignField(out, 'dateOfBirth', basic[3]);
      assignField(out, 'gender', basic[4]);
    }

    const loan = sections[1]?.fields ?? [];
    assignField(out, 'product', loan[0]);
    assignField(out, 'scheme', loan[1]);
    assignField(out, 'loanAmount', loan[2]);
    assignField(out, 'tenure', loan[3]);
    assignField(out, 'grossMonthlyIncome', loan[4]);
    assignField(out, 'existingMonthlyEMI', loan[5]);

    const source = sections[2]?.fields ?? [];
    assignField(out, 'sourceType', source[0]);
    assignField(out, 'sourceDescription', source[1]);
    assignField(out, 'preferredBranch', source[2]);

    const address = sections[3]?.fields ?? [];
    assignField(out, 'addressLine1', address[0]);
    assignField(out, 'addressLine2', address[1]);
    assignField(out, 'pinCode', address[2]);
    assignField(out, 'city', address[3]);
    assignField(out, 'district', address[4]);
    assignField(out, 'state', address[5]);
    assignField(out, 'country', address[6]);
    assignField(out, 'addressType', address[7]);

    const priority = sections[4]?.fields ?? [];
    assignField(out, 'priorityType', priority[0]);
    assignField(out, 'remark', priority[1]);

    return out;
  }

  private readDataset(nodeKey: string | null | undefined): DatasetUiConfig | null {
    const node = nodeKey ? this.getNode(nodeKey) : undefined;
    const c = node?.configuration;
    if (!c) return null;
    return {
      general: (c['general'] as DatasetUiConfig['general']) ?? {},
      columns: (c['columns'] as DatasetColumnConfig[]) ?? [],
      filters: (c['filters'] as DatasetFilterConfig[]) ?? [],
      actions: (c['actions'] as DatasetActionConfig[]) ?? [],
    };
  }

  private readChoice(
    nodeKey: string | null | undefined,
    optionsKey: 'applicantTypes' | 'statusOptions',
  ): ChoiceUiConfig | null {
    const node = nodeKey ? this.getNode(nodeKey) : undefined;
    const c = node?.configuration;
    if (!c) return null;
    let options: string[] = [];
    if (Array.isArray(c['options'])) {
      options = c['options'] as string[];
    } else if (Array.isArray(c[optionsKey])) {
      options = c[optionsKey] as string[];
    }
    let prompt: string | undefined;
    if (typeof c['description'] === 'string') {
      prompt = c['description'];
    } else if (typeof c['prompt'] === 'string') {
      prompt = c['prompt'];
    }
    return {
      prompt,
      fieldCode: typeof c['fieldCode'] === 'string' ? c['fieldCode'] : optionsKey,
      options,
      optionDescriptions:
        c['optionDescriptions'] && typeof c['optionDescriptions'] === 'object'
          ? (c['optionDescriptions'] as Record<string, string>)
          : undefined,
      continueLabel: typeof c['continueLabel'] === 'string' ? c['continueLabel'] : 'Continue',
      cancelLabel: typeof c['cancelLabel'] === 'string' ? c['cancelLabel'] : 'Cancel',
    };
  }

  private hasRenderableSections(form: FormDocument | undefined): boolean {
    const normalized = normalizePageFormDocument(form ?? null) ?? form;
    return (normalized?.sections?.length ?? 0) > 0;
  }

  private async enrichEmptyForm(
    form: FormDocument | undefined,
    formId: string,
  ): Promise<FormDocument | undefined> {
    if (this.hasRenderableSections(form)) {
      return form;
    }
    const entry = this.pack.getPack()?.manifest.forms.find((item) => item.formId === formId);
    const path = entry?.path ?? '';
    const match = /^(.*)-v(\d+)\.json$/i.exec(path);
    if (!match) {
      return form;
    }
    const [, base, version] = match;
    const declared = Number(version);
    const root = this.pack.getPack()?.basePath ?? '/assets/workflow-config';
    for (let next = declared + 12; next >= 1; next -= 1) {
      if (next === declared) continue;
      try {
        const candidate = await firstValueFrom(
          this.http.get<FormDocument>(`${root}/${base}-v${next}.json`),
        );
        if (this.hasRenderableSections(candidate)) {
          return candidate;
        }
      } catch {
        // Try the next published form version on disk.
      }
    }
    return form;
  }

  private readRecordDetail(nodeKey: string | null | undefined): RecordDetailUiConfig | null {
    const node = nodeKey ? this.getNode(nodeKey) : undefined;
    const c = node?.configuration;
    if (!c) return null;
    return {
      general: (c['general'] as RecordDetailUiConfig['general']) ?? {},
      tabs: ((c['tabs'] as RecordDetailUiConfig['tabs']) ?? []).map((t) => ({
        key: t.key,
        label: t.label,
        sections: t.sections,
      })),
      actions: ((c['actions'] as RecordDetailUiConfig['actions']) ?? []).map((a) => ({
        key: a.key,
        label: a.label,
        scope: a.scope,
        icon: a.icon,
      })),
      header: (c['header'] as RecordDetailUiConfig['header']) ?? undefined,
      milestones: (c['milestones'] as RecordDetailUiConfig['milestones']) ?? undefined,
    };
  }

  /** Workflow `forms.submit-form` node key for create or edit opportunity. */
  /** Domain → source-row keys for create-existing form nodes (`fieldMapping`). */
  createFormFieldMapping(nodeKey: string): Record<string, string> | null {
    const mapping = this.getNode(nodeKey)?.configuration?.['fieldMapping'];
    if (!mapping || typeof mapping !== 'object' || Array.isArray(mapping)) {
      return null;
    }
    return mapping as Record<string, string>;
  }

  /** Context key used to prefill create-existing forms (default `selectedRow`). */
  createFormPrefillSourceKey(nodeKey: string): string {
    const key = this.getNode(nodeKey)?.configuration?.['prefillFrom'];
    const resolved = coerceText(key ?? 'selectedRow');
    return resolved || 'selectedRow';
  }

  /** Pack fieldCode → party record path for create-existing form nodes. */
  createFormResponseFieldMapping(nodeKey: string): Record<string, string> | null {
    return this.editResponseFieldMapping(nodeKey);
  }

  /** GET dataSource for create-existing party prefill (`forms.load-form-*`). */
  createFormPartyDataSource(
    nodeKey: string,
    vars: PackDataSourceVars = {},
  ): Record<string, unknown> | null {
    if (!this.isCreateExistingFormNode(nodeKey)) {
      return null;
    }
    return this.formNodeDataSource(nodeKey, vars);
  }

  /** Pack fieldCode → opportunity record path for edit form nodes. */
  editResponseFieldMapping(nodeKey: string): Record<string, string> | null {
    const mapping = this.getNode(nodeKey)?.configuration?.['responseFieldMapping'];
    if (!mapping || typeof mapping !== 'object' || Array.isArray(mapping)) {
      return null;
    }
    return mapping as Record<string, string>;
  }

  submitNodeKey(
    applicantType: string | null | undefined,
    isEditMode: boolean,
  ): string {
    const discovered = discoverSubmitNodeKey(this.workflow, applicantType, isEditMode);
    if (discovered) {
      return discovered;
    }

    const corporate = String(applicantType ?? '').toLowerCase() === 'corporate';
    if (isEditMode) {
      return corporate ? 'submit_edit_corporate' : 'submit_edit_individual';
    }
    return corporate ? 'submit_corporate' : 'submit_individual';
  }

  /** Parsed payload field mapping from a submit-form workflow node. */
  submitPayloadMapping(nodeKey: string): SubmitFieldMapping[] {
    const config = this.getNode(nodeKey)?.configuration ?? {};
    return parseSubmitPayloadMapping(config['payloadMapping']);
  }

  /**
   * Resolved HTTP endpoint + method for a workflow submit-form node.
   * Substitutes `{{opportunityId}}` and rewrites absolute pack URLs to LOS paths.
   */
  resolveSubmitEndpointConfig(
    nodeKey: string,
    vars: PackDataSourceVars = {},
    options: { draft?: boolean } = {},
  ): { endpoint: string; method: string } {
    const config = this.getNode(nodeKey)?.configuration ?? {};
    let endpoint = coerceText(config['apiEndpoint'] ?? config['endpoint'] ?? '');
    let method = coerceText(config['apiMethod'] ?? config['method'] ?? '').toUpperCase();

    endpoint = substituteSubmitEndpointVars(endpoint, vars);
    endpoint = resolvePackEndpoint(endpoint);

    const opportunityId = vars['opportunityId'];
    const hasId = opportunityId != null && String(opportunityId).trim() !== '';

    return finalizeSubmitEndpointConfig({
      endpoint,
      method,
      hasId,
      opportunityId,
      isDraft: !!options.draft,
    });
  }

  /** Build API body from workflow payloadMapping + form/modal values. */
  buildSubmitBody(
    nodeKey: string,
    formValues: Record<string, unknown>,
    extras: Record<string, unknown> = {},
  ): Record<string, unknown> {
    const mappings = this.submitPayloadMapping(nodeKey);
    if (!mappings.length) {
      return { ...extras, ...formValues };
    }
    return buildSubmitPayload(mappings, formValues, extras);
  }
}

function substituteSubmitEndpointVars(endpoint: string, vars: PackDataSourceVars): string {
  let resolved = endpoint;
  for (const [key, value] of Object.entries(vars)) {
    resolved = resolved.replace(
      new RegExp(String.raw`\{\{${key}\}\}`, 'g'),
      value != null ? String(value) : '',
    );
  }
  return resolved;
}

type FinalizeSubmitEndpointArgs = {
  endpoint: string;
  method: string;
  hasId: boolean;
  opportunityId: PackDataSourceVars['opportunityId'];
  isDraft: boolean;
};

function finalizeSubmitEndpointConfig(
  args: FinalizeSubmitEndpointArgs,
): { endpoint: string; method: string } {
  let { endpoint, method } = args;
  const { hasId, opportunityId, isDraft } = args;

  if (!endpoint || /api\.example\.com/i.test(endpoint)) {
    return resolvePlaceholderSubmitEndpoint(hasId, opportunityId, isDraft, method);
  }

  if (isDraft && hasId) {
    return {
      endpoint: `${endpoint.replace(/\/$/, '')}/draft`,
      method: 'POST',
    };
  }

  if (hasId && !isDraft && isOpportunityUpdateEndpoint(endpoint, opportunityId)) {
    method = method === 'POST' || !method ? 'PATCH' : method;
  }

  return { endpoint, method: method || (hasId && !isDraft ? 'PATCH' : 'POST') };
}

function resolvePlaceholderSubmitEndpoint(
  hasId: boolean,
  opportunityId: PackDataSourceVars['opportunityId'],
  isDraft: boolean,
  method: string,
): { endpoint: string; method: string } {
  if (isDraft && hasId) {
    return { endpoint: `/opportunities/${opportunityId}/draft`, method: 'POST' };
  }
  if (hasId) {
    return { endpoint: `/opportunities/${opportunityId}`, method: 'PATCH' };
  }
  return { endpoint: '/opportunities', method: method || 'POST' };
}

function isOpportunityUpdateEndpoint(
  endpoint: string,
  opportunityId: string | number | boolean | undefined,
): boolean {
  const normalized = endpoint.replace(/\/$/, '');
  if (normalized.endsWith('/draft')) {
    return false;
  }

  const id = coerceText(opportunityId);
  if (id && (normalized === `/opportunities/${id}` || normalized.endsWith(`/opportunities/${id}`))) {
    return true;
  }

  return /\/opportunities\/[^/]+$/i.test(normalized);
}

function getByPath(obj: unknown, path: string): unknown {
  if (!obj || typeof obj !== 'object') return undefined;
  const parts = path.replace(/^\$\.?/, '').split('.').filter(Boolean);
  let cur: unknown = obj;
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

function formatPackListLabel(value: string): string {
  const text = value.trim();
  if (!text || text === '—') {
    return text || '—';
  }
  if (/^[A-Z0-9]{2,6}$/.test(text)) {
    return text;
  }
  return text
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function formatPackListStatus(status: string): string {
  const normalized = status.trim().toLowerCase().replaceAll('_', ' ');
  const labels: Record<string, string> = {
    reject: 'Reject',
    rejected: 'Reject',
    pending: 'Pending',
    sanction: 'Sanction',
    sanctioned: 'Sanction',
    disbursed: 'Disbursed',
    assigned: 'Assigned',
    converted: 'Converted',
    open: 'Open',
    draft: 'Draft',
  };
  if (labels[normalized]) {
    return labels[normalized];
  }
  return status
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

function formatPackListDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB');
}

function isOpportunityListDrawerFilter(column: DatasetColumnConfig): boolean {
  const path = coerceText(column.sourcePath).toLowerCase();
  const label = coerceText(column.label).toLowerCase();
  return (
    path === 'prioritycode' ||
    path === 'priority' ||
    label === 'priority' ||
    path === 'status' ||
    label === 'status'
  );
}

function listFilterParamName(column: DatasetColumnConfig): string {
  const path = coerceText(column.sourcePath).trim();
  if (path.toLowerCase() === 'priority' || path.toLowerCase() === 'prioritycode') {
    return 'priorityCode';
  }
  if (path) {
    return path;
  }
  return coerceText(column.columnKey) || 'filter';
}

function formatListFilterLabel(label: string): string {
  const trimmed = coerceText(label).trim();
  if (!trimmed) {
    return trimmed;
  }
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}

function normalizeListFilterQueryValue(paramName: string, value: string): string {
  if (paramName === 'priorityCode') {
    return value.toUpperCase();
  }
  return value;
}

/** Workflow decision edges compare against `Individual` / `Corporate` (title case). */
function normalizeApplicantTypeForWorkflow(value: unknown): string {
  const raw = coerceText(value).toLowerCase();
  if (raw === 'corporate') return 'Corporate';
  if (raw === 'individual') return 'Individual';
  return coerceText(value);
}

function normalizeWorkflowDetailActionKey(key: string): string {
  if (key === 'assign_rm') {
    return 'assign_to_rm';
  }
  return key;
}

function assignField(
  out: Record<string, FieldLabelUi>,
  key: string,
  field: FormFieldDef | undefined,
): void {
  if (!field) return;
  out[key] = {
    label: typeof field.label === 'string' ? field.label : key,
    placeholder: typeof field.placeholder === 'string' ? field.placeholder : undefined,
    required: field.required === true,
  };
}
